
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { isAuthenticated, getCurrentUser } from '@/utils/auth';
import DashboardLayout from '@/components/dashboard/DashboardLayout';
import BusinessMetricsPanel from '@/components/business/BusinessMetricsPanel';
import ReferralsOverviewPanel from '@/components/business/ReferralsOverviewPanel';
import RevenueChartPanel from '@/components/dashboard/RevenueChartPanel';
import LatestReferralsPanel from '@/components/business/LatestReferralsPanel';
import { User } from '@/types/userRole';
import { generateMockMonthlyReport } from '@/utils/mockData';

const BusinessDashboard: React.FC = () => {
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(true);
  const [currentUser, setCurrentUser] = useState<User | null>(null);

  useEffect(() => {
    const checkAuth = async () => {
      if (!isAuthenticated()) {
        navigate('/login');
        return;
      }

      const user = getCurrentUser();
      
      // Check if user is a business user
      if (user && user.role !== 'business' && user.role !== 'manager') {
        navigate('/dashboard');
        return;
      }
      
      setCurrentUser(user);
      setIsLoading(false);
    };

    checkAuth();
  }, [navigate]);

  // Mock data for dashboard
  const { data: reportData, chartData } = generateMockMonthlyReport();

  const totalReferrals = 152;
  const pendingReferrals = 23;
  const approvedReferrals = 89;
  const completedReferrals = 40;

  const mockMetrics = {
    referralsCount: totalReferrals,
    completionRate: Math.round((completedReferrals / totalReferrals) * 100),
    conversionRate: 58,
    totalEarnings: 4280,
    pendingEarnings: 1200
  };

  if (isLoading) {
    return (
      <DashboardLayout isLoading={true}>
        <div className="flex items-center justify-center h-screen">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="p-4 md:p-6">
        <h1 className="text-2xl font-bold mb-6">Business Dashboard</h1>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
          <BusinessMetricsPanel metrics={mockMetrics} />
          <ReferralsOverviewPanel
            totalReferrals={totalReferrals}
            pendingReferrals={pendingReferrals}
            approvedReferrals={approvedReferrals}
            completedReferrals={completedReferrals}
          />
        </div>
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <div className="lg:col-span-2">
            <RevenueChartPanel chartData={chartData} />
          </div>
          <div>
            <LatestReferralsPanel />
          </div>
        </div>
      </div>
    </DashboardLayout>
  );
};

export default BusinessDashboard;
